import { Users } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { CrowdSeason, UiLang } from "../lib/types";
import { FoldPanel } from "./FoldPanel";

interface Props {
  lang: UiLang;
  /** Ruta tallene gjelder. Panelet vises ikke uten en. */
  crowd: CrowdSeason;
}

/**
 * Når det er fullt av folk på ruta, og når det er tomt.
 *
 * ── Hvorfor dette står for seg selv ────────────────────────────────────────
 *
 * Fordi det ikke gjorde det, og da fantes det ikke. Tallene kom inn med
 * «når bør du komme?», der de ble til to setninger — «rolig» under 35 og
 * «fullt» over 80 — inne i en rad blant tolv andre. Alt mellom 36 og 79 sa
 * ingenting, og det er de fleste månedene på de fleste rutene. Verre: hele
 * panelet skjules i det en plan står, så tallene forsvant nettopp når man
 * hadde valgt ruta de gjaldt.
 *
 * Meldt fra bruk som «hvor kan jeg se det, eller er det ikke pushet ennå?».
 * Det *var* pushet. Det var bare ikke mulig å få øye på, og det er samme sak.
 *
 * ── Hvorfor tolv søyler og ikke ett tall ───────────────────────────────────
 *
 * Fordi formen er svaret. «Otta–Flåm er travel» er ikke sant om noe: ruta går
 * fra 4 i januar til 100 i juli. Og det motsatte er like viktig — Bodø–Tromsø
 * ligger på 34 i januar, fordi nordlyssesongen fyller Tromsø om vinteren. To
 * ruter, samme land, motsatt kurve. Et gjennomsnitt ville skjult begge.
 *
 * ── Hvorfor det står hva tallene ikke teller ───────────────────────────────
 *
 * SSB teller dem som overnatter. Dagsturister og cruisepassasjerer er ikke
 * med, og de er nettopp de som fyller kaia i Geiranger. Den som leser «rolig i
 * mai» og møter tre cruiseskip har ikke fått et unøyaktig tall — de har fått
 * et tall om noe annet enn det de spurte om. Derfor står forbeholdet som
 * tekst, ikke som en fotnote.
 */
export function CrowdCalendar({ lang, crowd }: Props) {
  const t = dictionaries[lang];
  const peak = t.monthName[crowd.peakMonth - 1];
  const quiet = t.monthName[crowd.quietestMonth - 1];

  /*
   * «1 ganger færre gjester» er ikke en setning. Rutene i dataene svinger fra
   * tre til tjuefem ganger, så dette treffer ingen av dem i dag — men tallet
   * kommer fra en henting som kjøres på nytt, og en rute uten sesong er en
   * fullt mulig fasit å få tilbake. Da skal appen si det framfor å regne det.
   */
  const summary = crowd.swing <= 1 ? t.crowdFlat(peak) : t.crowdSummary(peak, quiet, crowd.swing);

  return (
    <FoldPanel
      id="crowdCalendar"
      title={t.crowdTitle}
      icon={<Users size={14} />}
      summary={summary}
    >
      <p className="card-sub">{t.crowdHint}</p>

      {/*
        Svaret, også når panelet står åpent.

        `FoldPanel` viser `summary` bare når panelet er lagt sammen — altså
        overalt unntatt der brukeren faktisk leser. Det er nøyaktig samme feil
        som `MonthAnswer` alt har rettet én gang, og den ble gjort om igjen her
        før en test fant den: tolv søyler uten en setning som sier hva de betyr
        er et diagram man må tolke selv.
      */}
      <p className="crowd-verdict">{summary}</p>

      <div className="crowd-bars">
        {crowd.months.map((load, index) => {
          const month = index + 1;
          const isPeak = month === crowd.peakMonth;
          const isQuiet = month === crowd.quietestMonth;
          return (
            <div
              className={`crowd-bar ${isPeak ? "crowd-bar--peak" : ""} ${
                isQuiet ? "crowd-bar--quiet" : ""
              }`}
              key={month}
            >
              {/*
                Tallet står over søyla, ikke bare i høyden.

                En søyle kan leses av på et øyemål, og et øyemål er ikke et
                svar: forskjellen mellom 79 og 88 er den mellom «det går» og
                «book i god tid», og den ser lik ut som to streker.
              */}
              <span className="crowd-value">{load}</span>
              <div className="crowd-track" title={t.crowdBar(t.monthName[index], load)}>
                <span className="crowd-fill" style={{ height: `${load}%` }} />
              </div>
              <span className="crowd-month">{t.monthShort[index]}</span>
            </div>
          );
        })}
      </div>

      <p className="crowd-legend">
        <span className="crowd-key crowd-key--peak" /> {t.crowdPeakLabel}
        <span className="crowd-key crowd-key--quiet" /> {t.crowdQuietLabel}
      </p>

      {/*
        Regionen som svinger mest, navngitt ved siden av snittet.

        Et snitt over ruta skjuler nettopp det som er verdt å vite: på
        Bergensbanen går Hardanger fra 4 til 100 mens Oslo aldri er under 51.
        Den reisende passerer begge.
      */}
      <p className="note">{t.crowdBusiest(crowd.busiestRegion.name, crowd.busiestRegion.swing)}</p>
      <p className="note">{t.crowdRegions(crowd.regions.join(", "))}</p>
      <p className="note">{t.crowdCaveat}</p>
    </FoldPanel>
  );
}
